import { VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import styled from "styled-components/native";
import { SquareIconViewProps } from "./types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { useState } from "react";
import { css } from "styled-components/native";
import { getImageTypeFromUrl } from "@scenes/configurator/JewelryConfigurator/utils";
import { ButtonStyle } from "@scenes/configurator/JewelryConfigurator/types";
import TooltipButton from "@scenes/pdp/utils/TooltipButton";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import { Platform } from "react-native";
import { DefaultTheme } from "styled-components/native";

const SquareIconView = ({
  backgroundColor,
  borderColor,
  isSelected,
  item,
  buttonSize,
  onPress,
}: SquareIconViewProps) => {
  const { typography, theme, t } = useScreenOptionsHooks();
  const [isHovered, setIsHovered] = useState(false);
  const isUnavailable = !item.enabled;

  const ToolTipComponent = () => {
    return (
      <>
        <VDBText
          color={theme["core-01"]}
          type={typography["Minor-R"]}
          textAlign={"left"}
        >
          {t(
            "productDetailPage.jewelryConfigurator.disabledItemTooltip.text",
            "This option is not visible for the current selection."
          )}
        </VDBText>
      </>
    );
  };

  const renderComponent = () => {
    return (
      <ItemContainer
        onHoverIn={() => {
          if (!isSelected && theme.isLargeScreen) {
            setIsHovered(true);
          }
        }}
        onHoverOut={() => {
          if (theme.isLargeScreen) {
            setIsHovered(false);
          }
        }}
        buttonSize={buttonSize}
        onPress={() => {
          onPress(item);
        }}
      >
        {getImageTypeFromUrl(item?.icon_url ?? "") !== "svg" ? (
          <URLImageContainer
            isSelected={isSelected}
            backgroundColor={backgroundColor}
            borderColor={borderColor}
            isHovered={isHovered}
            isUnavailable={isUnavailable}
          >
            <VDBIcon
              color={theme["core-06"]}
              size={40}
              variant={IconsVariant.CustomUrl}
              uri={item?.icon_url ?? ""}
              name={undefined}
            />
          </URLImageContainer>
        ) : (
          <ImageView
            isSelected={isSelected}
            backgroundColor={backgroundColor}
            borderColor={borderColor}
            isHovered={isHovered}
            isUnavailable={isUnavailable}
          >
            <VDBIcon
              color={theme["core-06"]}
              size={40}
              variant={IconsVariant.CustomUrl}
              uri={item.icon_url ?? ""}
              name={undefined}
              avoidSVGCheck={Platform.OS === "web" ? true : false}
            />
          </ImageView>
        )}
        {buttonSize !== ButtonStyle.SMALL && (
          <LabelView>
            <VDBText
              color={
                isUnavailable
                  ? theme["core-04"]
                  : isSelected
                  ? theme["core-05"]
                  : theme["core-06"]
              }
              type={typography[isSelected ? "Minor-M" : "Minor-R"]}
              textAlign={"center"}
              numberOfLines={buttonSize === ButtonStyle.MEDIUM_01 ? 1 : 2}
            >
              {item.label}
            </VDBText>
          </LabelView>
        )}
      </ItemContainer>
    );
  };
  return (
    <>
      {isUnavailable && theme.isLargeScreen ? (
        <TooltipButton
          width={"auto"}
          children={renderComponent()}
          enableTooltip={true}
          renderComponent={ToolTipComponent()}
        />
      ) : (
        renderComponent()
      )}
    </>
  );
};

const ItemContainer = styled.Pressable<{
  buttonSize: ButtonStyle;
}>`
  height: ${({ buttonSize }: { buttonSize: ButtonStyle }) =>
    buttonSize === ButtonStyle.MEDIUM_01
      ? 74
      : buttonSize === ButtonStyle.MEDIUM_02
      ? 92
      : 48}px;
  width: ${({ buttonSize }: { buttonSize: ButtonStyle }) =>
    buttonSize === ButtonStyle.MEDIUM_01 || buttonSize === ButtonStyle.MEDIUM_02
      ? 75
      : 48}px;
  gap: ${SpaceType.Basic}px;
`;
const ImageView = styled.View<{
  isSelected: boolean;
  backgroundColor: any;
  borderColor: any;
  isHovered: boolean;
  isUnavailable?: boolean;
}>`
  border-radius: ${SpaceType.Basic}px;
  padding: ${SpaceType.ExtraSmall}px;
  background-color: ${({
    backgroundColor,
    isHovered,
    theme,
    isUnavailable,
    isSelected,
  }: {
    backgroundColor: string;
    isHovered: boolean;
    theme: DefaultTheme;
    isUnavailable: boolean;
    isSelected: boolean;
  }) =>
    isHovered || !isSelected
      ? theme["option-bg-static"]
      : isUnavailable
      ? theme["core-02"]
      : backgroundColor};
  align-items: center;
  justify-content: center;
  border-width: 1px;
  border-color: ${({
    borderColor,
    isHovered,
    theme,
    isUnavailable,
  }: {
    borderColor: string;
    isHovered: boolean;
    theme: DefaultTheme;
    isUnavailable: boolean;
  }) => (isHovered || isUnavailable ? theme["core-04"] : borderColor)};
  ${({ isUnavailable }: { isUnavailable: boolean }) =>
    isUnavailable &&
    css`
      border-style: dashed;
    `}
  height: 48px;
`;

const LabelView = styled.View`
  align-items: center;
`;

const URLImageContainer = styled.View<{
  isSelected: boolean;
  backgroundColor: any;
  borderColor: any;
  isHovered: boolean;
  isUnavailable?: boolean;
}>`
  border-width: 1px;
  border-color: ${({
    borderColor,
    isHovered,
    theme,
    isUnavailable,
    isSelected,
  }: {
    borderColor: string;
    isHovered: boolean;
    theme: DefaultTheme;
    isUnavailable: boolean;
    isSelected: boolean;
  }) =>
    isHovered || isUnavailable
      ? theme["core-04"]
      : isSelected
      ? borderColor
      : "transparent"};
  ${({ isUnavailable }: { isUnavailable: boolean }) =>
    isUnavailable &&
    css`
      border-style: dashed;
    `}
  border-radius: ${SpaceType.Basic}px;
  height: 48px;
  align-items: center;
  justify-content: center;
  ${({
    isHovered,
    theme,
    isUnavailable,
    isSelected,
  }: {
    isHovered: boolean;
    theme: DefaultTheme;
    isUnavailable: boolean;
    isSelected: boolean;
  }) =>
    Platform.OS !== "web"
      ? css`
          background-color: ${isHovered || !isSelected
            ? theme.transparent
            : isUnavailable
            ? theme["core-02"]
            : theme.transparent};
        `
      : css`
          background-color: transparent;
        `}
`;

export default SquareIconView;
